import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { mediaUrl, IMG } from '../lib/mediaUrl';
import { track } from '../lib/track';
import { Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useSeo } from '../lib/useSeo';
import PendingOrderBanner from '../components/PendingOrderBanner';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function CartPage() {
  const { items, removeItem, updateQuantity, replaceItems, subtotalCents } = useCart();

  // ?restore=<token> comes from a cart-reminder email. Rebuild the cart from
  // what's still for sale, then drop the token from the URL.
  const [params, setParams] = useSearchParams();
  const restoreToken = params.get('restore');
  const restoring = useRef(false);
  const [restoreNote, setRestoreNote] = useState('');
  useEffect(() => {
    if (!restoreToken || restoring.current) return;
    restoring.current = true;
    supabase.rpc('sv_restore_cart', { p_token: restoreToken }).then(({ data }) => {
      if (data?.length) {
        replaceItems(data.map((r) => ({
          product_id: r.product_id,
          name: r.name,
          slug: r.slug,
          price_cents: r.price_cents,
          image_url: r.storage_path ? mediaUrl(r.storage_path, IMG.card) : null,
          quantity: r.quantity,
        })));
        track('cart_restore', { meta: { items: data.length } });
      } else {
        setRestoreNote('Everything from that cart has sold. Take a look at what just came in.');
      }
      setParams({}, { replace: true });
    }, () => { setParams({}, { replace: true }); });
  }, [restoreToken, replaceItems, setParams]);

  // Step marker for the funnel. Subtotal rides along so drop-off can be read
  // against basket size, not just counts.
  useEffect(() => { track('cart_view', { value_cents: subtotalCents }); }, [subtotalCents]);
  const navigate = useNavigate();

  useSeo({
    title: 'Your Cart · Shiny Vault LGS',
    description: 'Review the items in your cart and check out at Shiny Vault LGS.',
    url: 'https://shinyvaultlgs.com/cart',
  });

  if (items.length === 0) {
    return (
      <div className="sv-container sv-page-top sv-page-bottom" style={{ maxWidth: 720 }}>
        <div style={{ textAlign: 'left' }}><PendingOrderBanner /></div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 10, textAlign: 'center' }}>
          {restoreToken ? 'Opening your cart…' : 'Your cart is empty'}
        </h1>
        {restoreNote && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: 6 }}>{restoreNote}</p>
        )}
        <div style={{ textAlign: 'center' }}>
          <Link to="/shop" className="sv-btn" style={{ marginTop: 10, display: 'inline-flex' }}>
            Browse the vault
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sv-container sv-page-top sv-page-bottom" style={{ maxWidth: 720 }}>
      <PendingOrderBanner />
      <h1 style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: 24 }}>Your cart</h1>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.map((item) => (
          <div
            key={item.product_id}
            className="sv-card"
            style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14 }}
          >
            {item.image_url && (
              <img src={item.image_url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{item.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{money(item.price_cents)} each</div>
            </div>
            <input
              type="number"
              min={1}
              value={item.quantity}
              onChange={(e) => updateQuantity(item.product_id, parseInt(e.target.value, 10) || 1)}
              className="sv-input"
              style={{ width: 64, textAlign: 'center' }}
            />
            <div style={{ fontWeight: 800, minWidth: 70, textAlign: 'right' }}>
              {money(item.price_cents * item.quantity)}
            </div>
            <button
              onClick={() => removeItem(item.product_id)}
              className="sv-btn-outline"
              style={{ padding: 8, borderRadius: 999 }}
              aria-label="Remove"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24, fontSize: '1.1rem', fontWeight: 800 }}>
        <span>Subtotal</span>
        {/* Plain number, no count-up. Animating the total made the one figure a
            shopper is nervous about tick and flicker before settling, which
            draws attention to the amount leaving their account at exactly the
            wrong moment. Motion belongs on things that delight, not on the
            price. */}
        <span>{money(subtotalCents)}</span>
      </div>
      <button
        onClick={() => navigate('/checkout')}
        className="sv-btn"
        style={{ width: '100%', marginTop: 20, padding: 14 }}
      >
        Continue to checkout
      </button>
    </div>
  );
}
